import { supabase, safeSupabaseCall } from "../supabaseClient";
import { notificationService } from "./notifications/notificationService";
import {
  shouldSuppressSupportPush,
  isSupportSchemaMismatchError,
} from "./supportChatPresenceService";
import logger from "../utils/logger";

const SUPPORT_CHAT_CONTEXT_SELECT = `
  id,
  user_id,
  user_last_viewed_at,
  admin_last_viewed_at,
  users_unified(full_name, class, email)
`;

const SUPPORT_CHAT_CONTEXT_FALLBACK_SELECT = `
  id,
  user_id,
  users_unified(full_name, class, email)
`;

const loadSupportChatContext = async (chatId) => {
  const loadChat = async (selectClause) =>
    supabase
      .from("support_chats")
      .select(selectClause)
      .eq("id", chatId)
      .single();

  let response = await loadChat(SUPPORT_CHAT_CONTEXT_SELECT);

  if (response.error && isSupportSchemaMismatchError(response.error)) {
    logger.warn("Support chat presence columns unavailable; using fallback context", {
      chatId,
      error_code: response.error.code || null,
    });
    response = await loadChat(SUPPORT_CHAT_CONTEXT_FALLBACK_SELECT);
  }

  if (response.error) {
    throw response.error;
  }

  return response.data || null;
};

/**
 * Получить активные чаты пользователя
 */
export const getUserChats = async () => {
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError || !user) {
    logger.error("Auth error in getUserChats:", authError);
    throw new Error("Требуется аутентификация");
  }

  const role = user.app_metadata?.role || user.role;
  const isSupportAdmin = role === "megaadmin";

  // Получаем все активные профили, связанные с этим email
  const { data: profiles } = await supabase
    .from("users_unified")
    .select("id")
    .eq("email", user.email)
    .eq("is_active", true);

  if ((!profiles || profiles.length === 0) && !isSupportAdmin) {
    throw new Error("Профиль пользователя не найден");
  }

  const profileIds = profiles?.map((p) => p.id) || [];

  // Добавляем самого себя (auth.uid()), если его нет в списке
  if (user.id && !profileIds.includes(user.id)) {
    profileIds.push(user.id);
  }

  let query = supabase.from("support_chats").select(`
    *,
    users_unified (
      full_name,
      class,
      email,
      telegram_id
    )
  `);

  if (!isSupportAdmin) {
    query = query.in("user_id", profileIds);
  }

  const { data: chats, error } = await query.order("updated_at", {
    ascending: false,
  });

  if (error) throw error;

  // Обогащаем чаты информацией о непрочитанных сообщениях
  const enrichedChats = await Promise.all(
    (chats || []).map(async (chat) => {
      const { count } = await supabase
        .from("support_messages")
        .select("*", { count: "exact", head: true })
        .eq("chat_id", chat.id)
        .eq("is_read", false)
        .eq("sender_role", isSupportAdmin ? "user" : "admin");

      return {
        ...chat,
        has_unread: (count || 0) > 0,
        unread_count: count || 0,
      };
    }),
  );

  return enrichedChats;
};

/**
 * Получить сообщения конкретного чата
 */
export const getChatMessages = async (chatId) => {
  return await safeSupabaseCall(() =>
    supabase
      .from("support_messages")
      .select("*")
      .eq("chat_id", chatId)
      .order("created_at", { ascending: true }),
  );
};

/**
 * Отметить все сообщения в чате как прочитанные
 * @param {string} chatId - ID чата
 * @param {string} readerRole - 'admin' или 'user' - кто читает
 */
export const markChatAsRead = async (chatId, readerRole = "admin") => {
  if (readerRole === "admin") {
    // Admin читает сообщения пользователя
    return await safeSupabaseCall(() =>
      supabase
        .from("support_messages")
        .update({ is_read: true })
        .eq("chat_id", chatId)
        .eq("sender_role", "user")
        .eq("is_read", false),
    );
  } else {
    // Пользователь читает сообщения админа
    return await safeSupabaseCall(() =>
      supabase
        .from("support_messages")
        .update({ is_read_by_user: true })
        .eq("chat_id", chatId)
        .eq("sender_role", "admin")
        .eq("is_read_by_user", false),
    );
  }
};

/**
 * Получить количество непрочитанных сообщений для админа (от пользователей)
 */
export const getAdminUnreadCount = async () => {
  const { count, error } = await supabase
    .from("support_messages")
    .select("*", { count: "exact", head: true })
    .eq("sender_role", "user")
    .eq("is_read", false);

  if (error) return 0;
  return count || 0;
};

/**
 * Получить количество непрочитанных сообщений для пользователя (от админа)
 */
export const getUserUnreadCount = async () => {
  const { count, error } = await supabase
    .from("support_messages")
    .select("*", { count: "exact", head: true })
    .eq("sender_role", "admin")
    .eq("is_read_by_user", false);

  if (error) return 0;
  return count || 0;
};

/**
 * Создать новый чат поддержки или вернуть существующий открытый
 */
export const createSupportChat = async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Требуется аутентификация");

  // Ищем все профили. Для создания чата предпочтительнее использовать тот,
  // чей ID совпадает с auth.uid(), или первый попавшийся.
  const { data: profiles, error: profileError } = await supabase
    .from("users_unified")
    .select("id, role")
    .eq("email", user.email)
    .eq("is_active", true);

  if (profileError) throw profileError;
  if (!profiles || profiles.length === 0) {
    throw new Error("Профиль пользователя не найден в системе");
  }

  // Приоритет:
  // 1. Профиль, где id === user.id
  // 2. Профиль с ролью admin/tutor (если есть)
  // 3. Первый профиль
  const profile =
    profiles.find((p) => p.id === user.id) ||
    profiles.find((p) => ["admin", "superadmin", "tutor"].includes(p.role)) ||
    profiles[0];

  const userId = profile.id;

  // Ищем уже созданные открытые чаты для ВСЕХ профилей пользователя
  const profileIds = profiles.map((p) => p.id);
  const { data: existingChat } = await supabase
    .from("support_chats")
    .select("*")
    .in("user_id", profileIds)
    .eq("status", "open")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (existingChat) {
    return existingChat;
  }

  // Если нет — создаем новый для выбранного профиля
  return await safeSupabaseCall(async () => {
    const { data, error } = await supabase
      .from("support_chats")
      .insert([{ user_id: userId, status: "open" }])
      .select()
      .single();

    if (error) {
      if (error.code === "23505" || error.code === "409") {
        const { data: retryData } = await supabase
          .from("support_chats")
          .select("*")
          .in("user_id", profileIds)
          .eq("status", "open")
          .maybeSingle();
        if (retryData) return retryData;
      }
      throw error;
    }
    return data;
  });
};

/**
 * Отправить сообщение в чат
 * role: 'user' по умолчанию, 'admin' для сотрудников поддержки
 */
export const sendSupportMessage = async (chatId, content, role = "user") => {
  if (!content || !content.trim()) {
    throw new Error("Сообщение не может быть пустым");
  }

  const result = await safeSupabaseCall(async () => {
    const { data, error } = await supabase
      .from("support_messages")
      .insert([
        {
          chat_id: chatId,
          content: content.trim(),
          sender_role: role,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  });

  let chat = null;
  try {
    chat = await loadSupportChatContext(chatId);
  } catch (error) {
    logger.error("Error loading support chat context:", error);
  }

  // Если написал пользователь, создаем уведомление для админов
  if (role === "user" && result) {
    try {
      if (chat) {
        await notificationService.createNotification({
          type: "support_message",
          title: "💬 Поддержка",
          message: `${chat.users_unified?.full_name || "Пользователь"}: ${content.substring(0, 50)}${content.length > 50 ? "..." : ""}`,
          student_id: chat.user_id,
        });
      }
    } catch (e) {
      logger.error("Error creating support notification:", e);
    }
  }

  if (result && chat) {
    const recipientRole = role === "user" ? "admin" : "user";
    const shouldSuppress = shouldSuppressSupportPush(chat, recipientRole);

    if (!shouldSuppress) {
      try {
        const body = {
          scenario: "support_chat_message",
          url: "/support",
          data: {
            chat_id: chatId,
            message_id: result.id,
            sender_role: role,
          },
        };

        if (recipientRole === "admin") {
          body.targetRoles = ["megaadmin"];
        } else {
          body.targetUserIds = [chat.user_id];
        }

        const { data: pushData, error: pushError } = await supabase.functions.invoke(
          "push-send",
          { body },
        );

        if (pushError || pushData?.success === false) {
          throw new Error(
            pushError?.message ||
              pushData?.error ||
              "Failed to send support push notification",
          );
        }
      } catch (error) {
        logger.error("Error creating support push notification:", error);
      }
    }
  }

  return result;
};

/**
 * Подписаться на новые сообщения в чате
 */
export const subscribeToChatMessages = (chatId, onMessage) => {
  return supabase
    .channel(`public:support_messages:chat_id=eq.${chatId}`)
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "support_messages",
        filter: `chat_id=eq.${chatId}`,
      },
      (payload) => {
        onMessage(payload.new);
      },
    )
    .subscribe();
};

export const supportService = {
  getUserChats,
  getChatMessages,
  createSupportChat,
  sendSupportMessage,
  subscribeToChatMessages,
  markChatAsRead,
  getAdminUnreadCount,
  getUserUnreadCount,
};

export default supportService;
